// -- [Try page for validating short URL codes]

import { FormEvent, useMemo, useState } from 'react'
import {
  getShortUrlBaseUrl,
  validateShortCode,
  type ShortUrlValidationResult,
} from '@/lib/shortUrlApi'

const CODE_PATTERN = /^[A-Za-z0-9]+$/

function normalizePrefillCandidate(raw: string): string {
  const t = raw.trim()
  return t && CODE_PATTERN.test(t) ? t : ''
}

// -- [Query ?code= / ?accessCode= / ?access= or path /try/{code} prefill the field]
function readTryPrefillCode(): string {
  if (typeof window === 'undefined') return ''
  const params = new URLSearchParams(window.location.search)
  const fromQuery =
    params.get('code')?.trim() ||
    params.get('accessCode')?.trim() ||
    params.get('access')?.trim() ||
    ''
  const fromQueryNorm = normalizePrefillCandidate(fromQuery)
  if (fromQueryNorm) return fromQueryNorm

  const path = window.location.pathname.replace(/\/+$/, '') || '/'
  const pathMatch = path.match(/^\/try\/([^/]+)$/)
  if (!pathMatch) return ''
  try {
    return normalizePrefillCandidate(decodeURIComponent(pathMatch[1]))
  } catch {
    return ''
  }
}

function statusTextColor(status: ShortUrlValidationResult['status'] | null): string {
  if (status === 'valid') return 'text-green-700'
  if (status === 'invalid' || status === 'expired') return 'text-amber-700'
  return 'text-red-700'
}

export function TryPage() {
  const urlPrefill = useMemo(() => readTryPrefillCode(), [])
  const [code, setCode] = useState(urlPrefill)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState<ShortUrlValidationResult | null>(null)
  const shortBaseUrl = useMemo(() => getShortUrlBaseUrl(), [])

  const showPrefillHint =
    urlPrefill !== '' && code.trim() === urlPrefill && result === null

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const normalized = code.trim()
    if (!normalized) {
      setResult({ status: 'invalid', message: 'Please enter a short code.' })
      return
    }
    if (!CODE_PATTERN.test(normalized)) {
      setResult({
        status: 'invalid',
        message: 'Short code can only contain letters and numbers.',
      })
      return
    }

    setIsSubmitting(true)
    setResult(null)

    const checkResult = await validateShortCode(normalized)
    setResult(checkResult)
    setIsSubmitting(false)

    if (checkResult.status === 'valid') {
      const redirectTo = checkResult.destination || `${shortBaseUrl}/${normalized}`
      window.setTimeout(() => {
        window.location.assign(redirectTo)
      }, 1200)
    }
  }

  return (
    <main className="min-h-screen bg-gradient-to-br from-brand-orange/10 via-white to-brand-orange/5 px-6 py-12">
      <div className="mx-auto w-full max-w-2xl">
        <a
          href="/"
          className="items-center text-sm font-medium text-brand-orange hover:underline"
          data-umami-event="Try page - Back to home"
        >
          Back to &nbsp;<strong>Drivible</strong>
        </a>

        <section className="mt-4 rounded-2xl border border-brand-orange/15 bg-white/90 p-6 shadow-sm backdrop-blur-[1px] md:p-8">
          <h1 className="text-3xl font-semibold tracking-tight text-gray-700">Enter your <strong className="font-bold text-gray-900">Dealership Login Code</strong></h1>
          <p className="mt-3 text-gray-600">
          You'll be redirected to our special login page
          </p>

          {showPrefillHint && (
            <div
              className="mt-5 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900"
              role="status"
            >
              Your access code from the link is filled in below. Press{' '}
              <strong className="font-semibold">Try Drivible</strong> to continue.
            </div>
          )}

          <form className="mt-6 space-y-4" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="shortCode" className="block text-sm font-medium text-gray-900">
                Code
              </label>
              <input
                id="shortCode"
                type="text"
                inputMode="text"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="e.g. qmn72"
                className={`mt-2 w-full rounded-lg border px-3 py-2 text-base text-gray-900 outline-none ring-0 transition focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/20 ${
                  showPrefillHint
                    ? 'border-green-400 bg-green-50/80 ring-1 ring-green-300/40'
                    : 'border-gray-300'
                }`}
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="inline-flex items-center justify-center rounded-lg bg-brand-orange px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-orange/75 disabled:cursor-not-allowed disabled:opacity-60"
              data-umami-event="Try page - Submit"
            >
              {isSubmitting ? 'Checking...' : 'Try Drivible'}
            </button>
          </form>


          {result && (
            <p className={`mt-5 text-sm font-medium ${statusTextColor(result.status)}`} role="status">
              {result.message}
            </p>
          )}

        {!result && (
          <>
          <p className="mt-3 text-sm text-gray-500">
            If you don't have a code, please contact{' '}
            <a
              href="mailto:hello@drivible.com"
              className="font-semibold text-brand-orange hover:underline whitespace-nowrap"
              data-umami-event="Try page - Contact email click"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                className="mr-1 inline-block h-4 w-4 align-[-0.125em]"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <path d="M2.5 5.5h15v9h-15z" />
                <path d="m3 6 7 5 7-5" />
              </svg>
              hello@drivible.com
            </a>{' '}
            or use our Setup form{' '}
            <a
              href="/#"
              className="font-semibold text-brand-orange hover:underline whitespace-nowrap"
              data-umami-event="Try page - Website signup link click"
            >
              <svg
                aria-hidden="true"
                viewBox="0 0 20 20"
                className="mr-1 inline-block h-4 w-4 align-[-0.125em]"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
              >
                <circle cx="10" cy="10" r="7" />
                <path d="M3 10h14M10 3a11.6 11.6 0 0 1 0 14M10 3a11.6 11.6 0 0 0 0 14" />
              </svg>
              on the website
            </a>
          </p>
           
           </>
        )}

        </section>


        <br />
        <p className="text-center text-sm text-gray-500"><a href="/" className='text-gray-900 hover:text-brand-orange hover:underline' aria-label="Go to Drivible home page" data-umami-event="Try page - Explanation text home click">Drivible</a> is a Document Storage and Management solution for Aussie Car Dealerships.</p>
        <p className="text-center text-sm text-gray-500">Just scan to email and you can search your documents.</p>

        <div className="mt-8 flex justify-center">
          <a href="/" aria-label="Go to Drivible home page" data-umami-event="Try page - Logo home click">
            <img
              src="/logos/drivible-logo.png"
              alt="Drivible"
              width={269}
              height={67}
              className="h-[67px] w-auto object-contain"
              title="Document Storage and Management for Aussie Car Dealerships"
            />
          </a>
        </div>
      </div>
    </main>
  )
}
